import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format, parseISO, subDays } from "date-fns";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MapOutlined from "@mui/icons-material/MapOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlined from "@mui/icons-material/TableChartOutlined";
import GridOnOutlined from "@mui/icons-material/GridOnOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import TeacherAvatar from "components/TeacherAvatar";
import Loading from "components/Loading";
import Swal from "sweetalert2";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";

import { fetchTeacherDetail } from "services/teacherDetailService";
import {
  exportTeacherDetailToCsv,
  exportTeacherDetailToExcel,
  exportTeacherDetailToPdf,
  teacherDetailFilename,
} from "utils/teacherDetailExport";
import { useTranslate } from "utils/i18n";
import { readIsTeacher, readIsLicensed } from "utils/teacherFlags";
import { useRegisterAssistantPage } from "assistant/useRegisterAssistantPage";
import { teacherDetailPath } from "utils/teacherRoutes";
import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";
import { BRIGHT_PALETTE } from "layouts/newmap/mapHelpers";
import { useSoftUIController } from "context";
import { isObserver } from "assistant/userRole";

const statTileSx = {
  p: 2,
  borderRadius: "14px",
  border: `1px solid ${figmaTokens.borderSubtle}`,
  background: `linear-gradient(145deg, rgba(102, 126, 234, 0.06) 0%, ${figmaTokens.surface} 55%)`,
  height: "100%",
  minHeight: 96,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

const sectionCardSx = {
  borderRadius: "16px",
  border: `1px solid ${figmaTokens.borderSubtle}`,
  boxShadow: "0 8px 28px rgba(15, 23, 42, 0.06)",
  overflow: "hidden",
  height: "100%",
};

function formatDateValue(value, pattern = "yyyy-MM-dd") {
  if (!value) return "—";
  try {
    const d = typeof value === "string" ? parseISO(value.replace(" ", "T")) : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
    return format(d, pattern);
  } catch {
    return String(value).slice(0, 19).replace("T", " ");
  }
}

function DetailRow({ label, value }) {
  return (
    <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} py={0.75}>
      <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, fontWeight: 600, flexShrink: 0 }}>
        {label}
      </SoftTypography>
      <SoftTypography variant="body2" sx={{ color: figmaTokens.textPrimary, fontWeight: 500, textAlign: "right" }}>
        {value ?? "—"}
      </SoftTypography>
    </SoftBox>
  );
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

function KpiTile({ label, value, hint }) {
  return (
    <SoftBox sx={statTileSx}>
      <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </SoftTypography>
      <SoftTypography variant="h4" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, my: 0.5, lineHeight: 1.2 }}>
        {value ?? "—"}
      </SoftTypography>
      {hint ? (
        <SoftTypography variant="caption" sx={{ color: figmaTokens.textSecondary }}>
          {hint}
        </SoftTypography>
      ) : null}
    </SoftBox>
  );
}

KpiTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hint: PropTypes.string,
};

function RankBadge({ label, rank, scope }) {
  if (rank == null) return null;
  return (
    <Chip
      size="small"
      label={`${label} #${rank}${scope ? ` · ${scope}` : ""}`}
      sx={{
        fontWeight: 700,
        bgcolor: "rgba(102, 126, 234, 0.12)",
        color: figmaTokens.accent,
        border: `1px solid rgba(102, 126, 234, 0.25)`,
      }}
    />
  );
}

RankBadge.propTypes = {
  label: PropTypes.string.isRequired,
  rank: PropTypes.number,
  scope: PropTypes.string,
};

function pickField(obj, ...keys) {
  if (!obj) return "";
  for (const key of keys) {
    const value = obj[key];
    if (value !== null && value !== undefined && String(value).trim() !== "") return String(value).trim();
  }
  return "";
}

function normalizeDocumentImageSrc(value) {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  if (raw.startsWith("data:") || raw.startsWith("blob:") || /^https?:\/\//i.test(raw)) {
    return raw;
  }

  // Mobile registration can return the image bytes as bare Base64. JPEG Base64 commonly
  // begins with `/9j/`, which must be detected before treating a leading slash as a URL path.
  const compact = raw.replace(/\s+/g, "");
  if (compact.length > 64 && /^[A-Za-z0-9+/=]+$/.test(compact)) {
    let mimeType = "image/jpeg";
    if (compact.startsWith("iVBOR")) mimeType = "image/png";
    else if (compact.startsWith("R0lGOD")) mimeType = "image/gif";
    else if (compact.startsWith("UklGR")) mimeType = "image/webp";
    return `data:${mimeType};base64,${compact}`;
  }

  return raw;
}

function documentImageUrl(document) {
  return normalizeDocumentImageSrc(document?.documentImage ?? document?.DocumentImage);
}

function documentFileExtension(src) {
  const mimeType = /^data:(image\/[a-z0-9.+-]+);/i.exec(src)?.[1]?.toLowerCase();
  if (mimeType === "image/png") return "png";
  if (mimeType === "image/gif") return "gif";
  if (mimeType === "image/webp") return "webp";
  return "jpg";
}

function TeacherDocuments({ documents, t }) {
  const [preview, setPreview] = useState(null);
  const [failedImages, setFailedImages] = useState({});

  const orderedDocuments = useMemo(
    () =>
      [...documents]
        .filter((document) => documentImageUrl(document))
        .sort(
          (a, b) =>
            Number(a?.displayOrder ?? a?.DisplayOrder ?? 0) -
            Number(b?.displayOrder ?? b?.DisplayOrder ?? 0)
        ),
    [documents]
  );

  if (orderedDocuments.length === 0) return null;

  return (
    <>
      <Card sx={{ ...sectionCardSx, mb: 3 }}>
        <SoftBox p={2.5}>
          <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <SoftBox display="flex" alignItems="center" gap={1}>
              <DescriptionOutlined sx={{ color: figmaTokens.accent }} />
              <SoftTypography variant="h6" fontWeight="bold">
                {t("teacher_detail.documents", "Documents")}
              </SoftTypography>
            </SoftBox>
            <Chip
              size="small"
              label={`${orderedDocuments.length} ${t("teacher_detail.documents_count", "document(s)")}`}
              variant="outlined"
            />
          </SoftBox>
          <Grid container spacing={2}>
            {orderedDocuments.map((document, index) => {
              const id = document?.id ?? document?.Id ?? index;
              const url = documentImageUrl(document);
              const createdDate = document?.createdDate ?? document?.CreatedDate;
              const imageFailed = Boolean(failedImages[id]);

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: "14px",
                      borderColor: figmaTokens.borderSubtle,
                      overflow: "hidden",
                      height: "100%",
                    }}
                  >
                    <SoftBox
                      component="button"
                      type="button"
                      onClick={() => setPreview({ url, index })}
                      aria-label={`${t("teacher_detail.view_document", "View document")} ${index + 1}`}
                      sx={{
                        width: "100%",
                        height: 180,
                        border: 0,
                        p: 0,
                        cursor: "pointer",
                        bgcolor: figmaTokens.pageBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        "&:hover img": { transform: "scale(1.03)" },
                      }}
                    >
                      {imageFailed ? (
                        <SoftBox textAlign="center" px={2}>
                          <DescriptionOutlined sx={{ fontSize: 44, color: figmaTokens.textMuted }} />
                          <SoftTypography variant="caption" display="block" sx={{ color: figmaTokens.textMuted }}>
                            {t("teacher_detail.document_preview_unavailable", "Preview unavailable")}
                          </SoftTypography>
                        </SoftBox>
                      ) : (
                        <SoftBox
                          component="img"
                          src={url}
                          alt={`${t("teacher_detail.document", "Document")} ${index + 1}`}
                          loading="lazy"
                          onError={() => setFailedImages((current) => ({ ...current, [id]: true }))}
                          sx={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 180ms ease" }}
                        />
                      )}
                    </SoftBox>
                    <SoftBox p={1.5} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                      <SoftBox minWidth={0}>
                        <SoftTypography variant="button" fontWeight="bold" display="block">
                          {t("teacher_detail.document", "Document")} {index + 1}
                        </SoftTypography>
                        {createdDate ? (
                          <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted }}>
                            {formatDateValue(createdDate, "dd MMM yyyy HH:mm")}
                          </SoftTypography>
                        ) : null}
                      </SoftBox>
                      <Tooltip title={t("teacher_detail.view_document", "View document")}>
                        <IconButton onClick={() => setPreview({ url, index })} size="small">
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </SoftBox>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </SoftBox>
      </Card>

      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {t("teacher_detail.document", "Document")} {preview ? preview.index + 1 : ""}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1, bgcolor: figmaTokens.pageBg, textAlign: "center" }}>
          {preview ? (
            <SoftBox
              component="img"
              src={preview.url}
              alt={`${t("teacher_detail.document", "Document")} ${preview.index + 1}`}
              sx={{ display: "block", maxWidth: "100%", maxHeight: "72vh", width: "auto", height: "auto", mx: "auto" }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          {preview ? (
            <SoftButton
              component="a"
              href={preview.url}
              download={`document-${preview.index + 1}.${documentFileExtension(preview.url)}`}
              variant="text"
              color="info"
              startIcon={<FileDownloadOutlined />}
            >
              {t("teacher_detail.download_document", "Download")}
            </SoftButton>
          ) : null}
          <SoftButton onClick={() => setPreview(null)} variant="gradient" color="info">
            {t("teacher_detail.close_document", "Close")}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

TeacherDocuments.propTypes = {
  documents: PropTypes.arrayOf(PropTypes.object).isRequired,
  t: PropTypes.func.isRequired,
};

function TeacherDetail() {
  const [controller] = useSoftUIController();
  const readOnly = isObserver(controller.user);
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const regFromQuery = searchParams.get("regNumber") || "";
  const navigate = useNavigate();
  const { t } = useTranslate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const exportRootRef = useRef(null);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchTeacherDetail({
        id: routeId && routeId !== "detail" ? routeId : undefined,
        regNumber: regFromQuery || undefined,
      });
      setData(payload);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setError(t("teacher_detail.not_found", "Teacher not found."));
      } else if (status === 400) {
        setError(t("teacher_detail.missing_id", "Teacher id or matricule is required."));
      } else {
        setError(t("teacher_detail.load_error", "Unable to load teacher profile."));
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [routeId, regFromQuery, t]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const profile = data?.profile ?? data?.Profile;
  const isTeacher = readIsTeacher(profile?.isTeacher ?? profile?.IsTeacher);
  const isLicensed = readIsLicensed(profile?.isLicensed ?? profile?.IsLicensed);
  const assignment = data?.currentAssignment;
  const summary = data?.summary;
  const ranking = data?.ranking;
  const charts = data?.charts;
  const recentAttendance = data?.recentAttendance ?? [];
  const documentImages = profile?.documentImages ?? profile?.DocumentImages ?? [];

  const teacherId = profile?.id ?? (routeId && routeId !== "detail" ? routeId : null);
  const regNumber = profile?.regNumber ?? regFromQuery;

  const assistantPageSnapshot = useMemo(
    () => ({
      pageKey: "teacher-detail",
      route: teacherDetailPath(teacherId, regNumber) || `/enseignant/${routeId || "detail"}`,
      selection: profile
        ? { teacherId, regNumber, name: profile.fullName || [profile.preName, profile.name].filter(Boolean).join(" ") }
        : { teacherId, regNumber },
    }),
    [teacherId, regNumber, profile, routeId]
  );
  useRegisterAssistantPage(assistantPageSnapshot);

  const monthlyChart = useMemo(() => {
    const rows = charts?.attendanceByMonth ?? [];
    return {
      labels: rows.map((r) => r.label ?? `${r.year}-${String(r.month).padStart(2, "0")}`),
      datasets: [{ label: t("teacher_detail.chart.monthly", "Attendance"), color: "info", data: rows.map((r) => r.count ?? 0) }],
    };
  }, [charts, t]);

  const dayOfWeekChart = useMemo(() => {
    const rows = charts?.attendanceByDayOfWeek ?? [];
    return {
      labels: rows.map((r) => r.day ?? "—"),
      datasets: [{ label: t("teacher_detail.chart.by_day", "By day"), color: "dark", data: rows.map((r) => r.count ?? 0) }],
    };
  }, [charts, t]);

  const establishmentChart = useMemo(() => {
    const rows = charts?.attendanceByEstablishment ?? [];
    const colors = rows.map((_, i) => BRIGHT_PALETTE[i % BRIGHT_PALETTE.length]);
    return {
      labels: rows.map((r) => r.estabName ?? "—"),
      datasets: {
        label: t("teacher_detail.chart.by_establishment", "By establishment"),
        data: rows.map((r) => r.count ?? 0),
        backgroundColors: colors,
      },
      cutout: "55%",
    };
  }, [charts, t]);

  const mapLink = useMemo(() => {
    if (!teacherId && !regNumber) return null;

    const params = new URLSearchParams();
    if (teacherId) params.set("teacherId", String(teacherId));
    if (regNumber) params.set("regNumber", regNumber);

    const estabId = pickField(
      assignment,
      "estabId",
      "EstabId",
      "establishmentId",
      "EstablishmentId"
    );
    if (estabId) params.set("estabId", estabId);

    const estabName = pickField(assignment, "estabName", "EstName", "establishment");
    if (estabName) params.set("estabName", estabName);

    const estabCode = pickField(assignment, "estabCode", "Code", "code");
    if (estabCode) params.set("estabCode", estabCode);

    const provinceName =
      pickField(ranking, "provinceName", "ProvinceName") ||
      pickField(assignment, "provinceName", "ProvinceName");
    const departmentName =
      pickField(ranking, "departmentName", "DepartmentName") ||
      pickField(assignment, "departmentName", "DistrictName");
    const subPrefectureName = pickField(
      assignment,
      "subprefectureName",
      "subPrefectureName",
      "SPName",
      "SpName"
    );

    if (provinceName) params.set("provinceName", provinceName);
    if (departmentName) params.set("departmentName", departmentName);
    if (subPrefectureName) params.set("subPrefectureName", subPrefectureName);

    const lastAttendance = summary?.lastAttendanceDate;
    if (lastAttendance) {
      try {
        const end = format(parseISO(String(lastAttendance).replace(" ", "T")), "yyyy-MM-dd");
        const start = format(subDays(parseISO(end), 90), "yyyy-MM-dd");
        params.set("startDate", start);
        params.set("endDate", end);
      } catch {
        /* ignore invalid date */
      }
    }

    return `/carte-enseignants?${params.toString()}`;
  }, [teacherId, regNumber, assignment, ranking, summary]);

  const exportLabels = useMemo(
    () => ({
      fullName: t("assignments.table.teacher_name", "Teacher name"),
      regNumber: t("teachers.table.reg", "Matricule"),
      gender: t("teachers.table.gender", "Gender"),
      dob: t("teachers.dob", "Date of birth"),
      regDate: t("teachers.reg_date", "Registration date"),
      degree: t("assignments.table.teacher_degree", "Degree"),
      status: t("teachers.table.status", "Status"),
      phone: t("teachers.table.phone", "Phone"),
      whatsapp: t("teachers.table.whatsapp", "WhatsApp"),
      email: t("teachers.table.email", "Email"),
      recruiter: t("teachers.recruiter", "Recruiter"),
      licensed: t("teacher_detail.licensed", "Licensed"),
      allocated: t("teacher_detail.allocated", "Assigned"),
      hasAttendance: t("teacher_detail.has_attendance", "Has attendance"),
      score: t("teacher_detail.score", "Score"),
      lastScoreDate: t("teacher_detail.last_score", "Last score date"),
      totalAttendance: t("teacher_detail.kpi.total", "Total attendance"),
      thisMonth: t("teacher_detail.kpi.this_month", "This month"),
      thisWeek: t("teacher_detail.kpi.this_week", "This week"),
      lastAttendanceDate: t("teacher_detail.kpi.last_seen", "Last seen"),
      daysSinceLastAttendance: t("teacher_detail.export_days_since", "Days since last attendance"),
      firstAttendanceDate: t("teacher_detail.first_attendance", "First attendance"),
      averageAttendancesPerMonth: t("teacher_detail.avg_per_month", "Avg. per month"),
      uniqueEstablishments: t("teacher_detail.unique_establishments", "Establishments"),
      attendanceWithinRadius: t("teacher_detail.export_within_radius_count", "Within radius count"),
      attendanceOutsideRadius: t("teacher_detail.export_outside_radius_count", "Outside radius count"),
      attendanceWithinRadiusPercent: t("teacher_detail.kpi.within_radius", "Within radius"),
      establishment: t("teacher_detail.establishment", "Establishment"),
      estabType: t("teacher_detail.estab_type", "Type"),
      assignedDate: t("teacher_detail.assigned_date", "Assigned"),
      location: t("teacher_detail.location", "Location"),
      latitude: t("teacher_detail.export_latitude", "Latitude"),
      longitude: t("teacher_detail.export_longitude", "Longitude"),
      notAssigned: t("teacher_detail.not_assigned", "Not currently assigned."),
      assignmentStatus: t("teacher_detail.export_assignment_status", "Assignment status"),
      overallRank: t("teacher_detail.rank.overall", "Overall"),
      provinceRank: t("teacher_detail.rank.province", "Province"),
      provinceName: t("assignments.province", "Province"),
      departmentRank: t("teacher_detail.rank.department", "Department"),
      departmentName: t("assignments.department", "Department"),
      date: t("home.table.attend_date", "Date"),
      dayOfWeek: t("teacher_detail.day", "Day"),
      distanceMeters: t("home.table.distance", "Distance"),
      radiusStatus: t("teacher_detail.radius_status", "Radius"),
      inRadius: t("map.kpi.in_radius", "In radius"),
      outRadius: t("map.kpi.out_radius", "Out of radius"),
      imei: t("home.table.imei", "IMEI"),
      period: t("teacher_detail.export_period", "Period"),
      count: t("teacher_detail.export_count", "Count"),
      sectionProfile: t("teacher_detail.export_section_profile", "Profile"),
      sectionSummary: t("teacher_detail.export_section_summary", "Summary"),
      sectionAssignment: t("teacher_detail.export_section_assignment", "Assignment"),
      sectionRanking: t("teacher_detail.export_section_ranking", "Ranking"),
      sheetOverview: t("teacher_detail.export_sheet_overview", "Overview"),
      sheetAttendance: t("teacher_detail.export_sheet_attendance", "Attendance"),
      sheetMonthly: t("teacher_detail.export_sheet_monthly", "Monthly"),
      sheetByDay: t("teacher_detail.export_sheet_by_day", "By day"),
      sheetByEstablishment: t("teacher_detail.export_sheet_by_estab", "By establishment"),
      noAttendance: t("teacher_detail.no_attendance", "No attendance records."),
      noData: t("teacher_detail.export_no_data", "No data"),
      field: t("teacher_detail.export_field", "Field"),
      value: t("teacher_detail.export_value", "Value"),
    }),
    [t]
  );

  const closeExportMenu = () => setExportMenuAnchor(null);

  const showExportError = (message) => {
    Swal.fire({
      icon: "error",
      title: t("teacher_detail.export_error", "Export failed"),
      text: message,
      confirmButtonColor: figmaTokens.accent,
    });
  };

  const handleExportCsv = () => {
    closeExportMenu();
    if (!data) return;
    try {
      exportTeacherDetailToCsv(data, exportLabels, teacherDetailFilename(regNumber, "csv"));
    } catch (err) {
      console.error("CSV export failed", err);
      showExportError(t("teacher_detail.export_csv_error", "Could not create the CSV file."));
    }
  };

  const handleExportExcel = () => {
    closeExportMenu();
    if (!data) return;
    try {
      exportTeacherDetailToExcel(data, exportLabels, teacherDetailFilename(regNumber, "xlsx"));
    } catch (err) {
      console.error("Excel export failed", err);
      showExportError(t("teacher_detail.export_excel_error", "Could not create the Excel file."));
    }
  };

  const handleExportPdf = async () => {
    closeExportMenu();
    if (!data || !exportRootRef.current) return;
    setExporting(true);
    try {
      const result = await exportTeacherDetailToPdf({
        element: exportRootRef.current,
        data,
        labels: exportLabels,
        meta: {
          title: t("teacher_detail.export_pdf_title", "Teacher profile"),
          subtitle: [profile?.fullName, profile?.regNumber].filter(Boolean).join(" · "),
          pageLabel: t("teacher_detail.export_page", "Page"),
        },
        filename: teacherDetailFilename(regNumber, "pdf"),
      });
      if (result?.mode === "data-fallback") {
        Swal.fire({
          icon: "info",
          title: t("teacher_detail.export_pdf_fallback_title", "PDF created"),
          text: t(
            "teacher_detail.export_pdf_fallback_text",
            "A simplified PDF was generated because the visual capture was blocked on this browser."
          ),
          confirmButtonColor: figmaTokens.accent,
          timer: 3500,
        });
      }
    } catch (err) {
      console.error("PDF export failed", err);
      showExportError(t("teacher_detail.export_pdf_error", "Could not create the PDF file."));
    } finally {
      setExporting(false);
    }
  };

  return (
    <DashboardLayout>
      {loading && <Loading />}
      <DashboardNavbar />
      <SoftBox py={3} px={{ xs: 2, sm: 3 }}>
        <SoftBox display="flex" alignItems="center" gap={1} mb={2}>
          <Tooltip title={t("teacher_detail.back", "Back")}>
            <IconButton onClick={() => navigate(-1)} sx={{ border: `1px solid ${figmaTokens.borderSubtle}` }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <SoftBox flex={1}>
            <SoftTypography variant="h5" fontWeight="bold" sx={{ color: figmaTokens.pageTitle }}>
              {t("teacher_detail.title", "Teacher profile")}
            </SoftTypography>
            {profile?.fullName && (
              <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary }}>
                {profile.fullName}
                {profile.regNumber ? ` · ${profile.regNumber}` : ""}
              </SoftTypography>
            )}
          </SoftBox>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {profile ? (
              <>
                <SoftButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  startIcon={<FileDownloadOutlined />}
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  disabled={exporting}
                >
                  {t("teacher_detail.export", "Export")}
                </SoftButton>
                <Menu
                  anchorEl={exportMenuAnchor}
                  open={Boolean(exportMenuAnchor)}
                  onClose={closeExportMenu}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem onClick={handleExportPdf} disabled={exporting}>
                    <ListItemIcon>
                      <PictureAsPdfOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t("teacher_detail.export_pdf", "PDF (page)")}</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleExportCsv}>
                    <ListItemIcon>
                      <TableChartOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t("teacher_detail.export_csv", "CSV (data)")}</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleExportExcel}>
                    <ListItemIcon>
                      <GridOnOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t("teacher_detail.export_excel", "Excel (data)")}</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : null}
            {teacherId ? (
              <SoftButton
                component={Link}
                to={`/histoire-des-enseignants/${encodeURIComponent(teacherId)}`}
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<HistoryOutlined />}
              >
                {t("teacher_detail.view_history", "History")}
              </SoftButton>
            ) : null}
            {!readOnly && mapLink ? (
              <SoftButton component={Link} to={mapLink} variant="outlined" color="info" size="small" startIcon={<MapOutlined />}>
                {t("teacher_detail.view_map", "View on map")}
              </SoftButton>
            ) : null}
            <SoftButton variant="gradient" color="info" size="small" onClick={loadDetail} startIcon={<RefreshOutlined />}>
              {t("teacher_detail.refresh", "Refresh")}
            </SoftButton>
          </Stack>
        </SoftBox>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>
            {error}
          </Alert>
        )}

        {!loading && !error && profile && (
          <SoftBox id="teacher-detail-export-root" ref={exportRootRef}>
            <Card sx={{ ...sectionCardSx, mb: 3 }}>
              <SoftBox
                p={3}
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                gap={3}
                sx={{
                  background: `linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, ${figmaTokens.surface} 48%)`,
                }}
              >
                <TeacherAvatar
                  teacher={profile}
                  size="xl"
                  shadow="md"
                  sx={{
                    width: 88,
                    height: 88,
                    fontSize: "1.75rem",
                    border: `3px solid ${figmaTokens.surface}`,
                    boxShadow: "0 8px 24px rgba(102, 126, 234, 0.25)",
                  }}
                />
                <SoftBox flex={1} minWidth={0}>
                  <SoftTypography variant="h4" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
                    {profile.fullName || "—"}
                  </SoftTypography>
                  <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mb: 1.5 }}>
                    {t("teachers.table.reg", "Matricule")}: {profile.regNumber ?? "—"}
                  </SoftTypography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {profile.status ? (
                      <Chip size="small" label={profile.status} sx={{ fontWeight: 600 }} />
                    ) : null}
                    {profile.degree ? (
                      <Chip size="small" variant="outlined" label={profile.degree} />
                    ) : null}
                    <Chip
                      size="small"
                      label={
                        isTeacher
                          ? t("teachers.type.teacher", "Teacher")
                          : t("teachers.type.staff", "Staff")
                      }
                      sx={{ fontWeight: 600 }}
                      color={isTeacher ? "primary" : "default"}
                      variant={isTeacher ? "filled" : "outlined"}
                    />
                    {isLicensed ? (
                      <Chip size="small" color="success" label={t("teacher_detail.licensed", "Licensed")} />
                    ) : (
                      <Chip
                        size="small"
                        label={t("teacher_detail.unlicensed", "Unlicensed")}
                        sx={{ fontWeight: 600, bgcolor: "#fef3c7", color: "#92400e", border: "1px solid #fbbf24" }}
                      />
                    )}
                    {profile.isAllocated ? (
                      <Chip size="small" color="info" label={t("teacher_detail.allocated", "Assigned")} />
                    ) : null}
                    {profile.hasAttendance ? (
                      <Chip size="small" color="primary" label={t("teacher_detail.has_attendance", "Has attendance")} />
                    ) : null}
                    {profile.score != null ? (
                      <Chip size="small" label={`${t("teacher_detail.score", "Score")}: ${profile.score}`} sx={{ fontWeight: 700 }} />
                    ) : null}
                  </Stack>
                </SoftBox>
                <SoftBox minWidth={{ md: 220 }}>
                  <DetailRow label={t("teachers.table.phone", "Téléphone")} value={profile.phone} />
                  <DetailRow label={t("teachers.table.whatsapp", "WhatsApp")} value={profile.whatsapp} />
                  <DetailRow label={t("teachers.table.email", "Email")} value={profile.email} />
                  <DetailRow label={t("teachers.recruiter", "Recruteur")} value={profile.recruiter} />
                </SoftBox>
              </SoftBox>
            </Card>

            <TeacherDocuments documents={documentImages} t={t} />

            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiTile label={t("teacher_detail.kpi.total", "Total attendance")} value={summary?.totalAttendance} />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiTile label={t("teacher_detail.kpi.this_month", "This month")} value={summary?.thisMonth} />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiTile label={t("teacher_detail.kpi.this_week", "This week")} value={summary?.thisWeek} />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiTile
                  label={t("teacher_detail.kpi.last_seen", "Last seen")}
                  value={summary?.daysSinceLastAttendance != null ? `${summary.daysSinceLastAttendance}d` : "—"}
                  hint={formatDateValue(summary?.lastAttendanceDate, "dd MMM yyyy")}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiTile
                  label={t("teacher_detail.kpi.within_radius", "Within radius")}
                  value={
                    summary?.attendanceWithinRadiusPercent != null
                      ? `${Number(summary.attendanceWithinRadiusPercent).toFixed(1)}%`
                      : "—"
                  }
                  hint={
                    summary?.attendanceWithinRadius != null
                      ? `${summary.attendanceWithinRadius} / ${(summary.attendanceWithinRadius ?? 0) + (summary.attendanceOutsideRadius ?? 0)}`
                      : undefined
                  }
                />
              </Grid>
            </Grid>

            <Grid container spacing={2.5} mb={3}>
              <Grid item xs={12} md={4}>
                <Card sx={sectionCardSx}>
                  <SoftBox p={2.5}>
                    <SoftTypography variant="h6" fontWeight="bold" mb={1.5}>
                      {t("teacher_detail.section.profile", "Profile")}
                    </SoftTypography>
                    <DetailRow
                      label={t("teachers.table.type", "Type")}
                      value={
                        isTeacher
                          ? t("teachers.type.teacher", "Teacher")
                          : t("teachers.type.staff", "Staff")
                      }
                    />
                    <DetailRow
                      label={t("teachers.table.license", "License")}
                      value={
                        isLicensed
                          ? t("teacher_detail.licensed", "Licensed")
                          : t("teacher_detail.unlicensed", "Unlicensed")
                      }
                    />
                    {!isLicensed ? (
                      <SoftBox mt={1}>
                        <Link to="/teacher-license" style={{ textDecoration: "none" }}>
                          <SoftTypography variant="caption" sx={{ color: figmaTokens.accent, fontWeight: 700 }}>
                            {t("teacher_detail.open_licenses", "Open Teacher Licenses to activate")}
                          </SoftTypography>
                        </Link>
                      </SoftBox>
                    ) : null}
                    <DetailRow label={t("teachers.table.gender", "Sexe")} value={profile.gender} />
                    <DetailRow label={t("teachers.dob", "Date de naissance")} value={formatDateValue(profile.dob)} />
                    <DetailRow label={t("teachers.reg_date", "Date d'inscription")} value={formatDateValue(profile.regDate, "dd MMM yyyy HH:mm")} />
                    <DetailRow label={t("teacher_detail.first_attendance", "First attendance")} value={formatDateValue(summary?.firstAttendanceDate, "dd MMM yyyy")} />
                    <DetailRow
                      label={t("teacher_detail.avg_per_month", "Avg. per month")}
                      value={summary?.averageAttendancesPerMonth != null ? Number(summary.averageAttendancesPerMonth).toFixed(1) : "—"}
                    />
                    <DetailRow label={t("teacher_detail.unique_establishments", "Establishments")} value={summary?.uniqueEstablishments} />
                  </SoftBox>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={sectionCardSx}>
                  <SoftBox p={2.5}>
                    <SoftTypography variant="h6" fontWeight="bold" mb={1.5}>
                      {t("teacher_detail.section.assignment", "Current assignment")}
                    </SoftTypography>
                    {assignment?.isAssigned ? (
                      <>
                        <DetailRow label={t("teacher_detail.establishment", "Establishment")} value={assignment.estabName} />
                        <DetailRow label={t("teacher_detail.estab_type", "Type")} value={assignment.estabType} />
                        <DetailRow label={t("teacher_detail.assigned_date", "Assigned")} value={formatDateValue(assignment.assignedDate)} />
                        <DetailRow label={t("teacher_detail.location", "Location")} value={assignment.locationLabel} />
                        {!readOnly && assignment.latitude && assignment.longitude ? (
                          <SoftButton
                            component={Link}
                            to={mapLink || "/carte-enseignants"}
                            variant="text"
                            color="info"
                            size="small"
                            sx={{ mt: 1, px: 0 }}
                            startIcon={<MapOutlined fontSize="small" />}
                          >
                            {assignment.latitude}, {assignment.longitude}
                          </SoftButton>
                        ) : null}
                      </>
                    ) : (
                      <SoftTypography variant="body2" sx={{ color: figmaTokens.textMuted }}>
                        {t("teacher_detail.not_assigned", "Not currently assigned.")}
                      </SoftTypography>
                    )}
                  </SoftBox>
                </Card>
              </Grid>
              {!readOnly && <Grid item xs={12} md={4}>
                <Card sx={sectionCardSx}>
                  <SoftBox p={2.5}>
                    <SoftTypography variant="h6" fontWeight="bold" mb={1.5}>
                      {t("teacher_detail.section.ranking", "Ranking")}
                    </SoftTypography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
                      <RankBadge label={t("teacher_detail.rank.overall", "Overall")} rank={ranking?.overallRank} />
                      <RankBadge label={t("teacher_detail.rank.province", "Province")} rank={ranking?.provinceRank} scope={ranking?.provinceName} />
                      <RankBadge label={t("teacher_detail.rank.department", "Department")} rank={ranking?.departmentRank} scope={ranking?.departmentName} />
                    </Stack>
                    <DetailRow label={t("teacher_detail.score", "Score")} value={ranking?.score ?? profile.score} />
                    {profile.lastScoreDate ? (
                      <DetailRow label={t("teacher_detail.last_score", "Last score date")} value={formatDateValue(profile.lastScoreDate, "dd MMM yyyy HH:mm")} />
                    ) : null}
                  </SoftBox>
                </Card>
              </Grid>}
            </Grid>

            <Grid container spacing={2.5} mb={3}>
              <Grid item xs={12} lg={6}>
                <DefaultLineChart
                  title={t("teacher_detail.chart.monthly_title", "Monthly attendance trend")}
                  description={t("teacher_detail.chart.monthly_desc", "Check-ins over the selected period")}
                  height="16rem"
                  chart={monthlyChart}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <VerticalBarChart
                  title={t("teacher_detail.chart.day_title", "By day of week")}
                  description={t("teacher_detail.chart.day_desc", "Distribution across weekdays")}
                  height="16rem"
                  chart={dayOfWeekChart}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <DefaultDoughnutChart
                  title={t("teacher_detail.chart.estab_title", "By establishment")}
                  description={t("teacher_detail.chart.estab_desc", "Share of check-ins per school")}
                  height="16rem"
                  chart={establishmentChart}
                  showLegend
                />
              </Grid>
            </Grid>

            <Card sx={sectionCardSx}>
              <SoftBox p={2.5}>
                <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <SoftTypography variant="h6" fontWeight="bold">
                    {t("teacher_detail.recent_attendance", "Recent attendance")}
                  </SoftTypography>
                  <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted }}>
                    {recentAttendance.length} {t("teacher_detail.records", "records")}
                  </SoftTypography>
                </SoftBox>
                <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ borderRadius: "12px", borderColor: figmaTokens.borderSubtle }}>
                  <Table
                    size="small"
                    stickyHeader
                    sx={{
                      ...directoryTableHeadStickyOverridesSx,
                      ...directoryTableBodyCellDefaultsSx,
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={directoryTableHeaderCellSx}>{t("home.table.attend_date", "Date")}</TableCell>
                        <TableCell sx={directoryTableHeaderCellSx}>{t("teacher_detail.establishment", "Establishment")}</TableCell>
                        <TableCell sx={directoryTableHeaderCellSx}>{t("teacher_detail.day", "Day")}</TableCell>
                        <TableCell sx={directoryTableHeaderCellSx}>{t("home.table.distance", "Distance")}</TableCell>
                        <TableCell sx={directoryTableHeaderCellSx}>{t("teacher_detail.radius_status", "Radius")}</TableCell>
                        <TableCell sx={directoryTableHeaderCellSx}>{t("home.table.imei", "IMEI")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentAttendance.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4, color: figmaTokens.textMuted }}>
                            {t("teacher_detail.no_attendance", "No attendance records.")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        recentAttendance.map((row) => (
                          <TableRow key={row.attendanceId ?? `${row.date}-${row.time}`} hover>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                              {row.date} {row.time}
                            </TableCell>
                            <TableCell>
                              {row.estabName ?? "—"}
                              {row.estabType ? (
                                <SoftTypography component="span" variant="caption" sx={{ color: figmaTokens.textMuted, ml: 0.5 }}>
                                  ({row.estabType})
                                </SoftTypography>
                              ) : null}
                            </TableCell>
                            <TableCell>{row.dayOfWeek ?? "—"}</TableCell>
                            <TableCell>
                              {row.distanceMeters != null ? `${Number(row.distanceMeters).toFixed(0)} m` : "—"}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={
                                  row.isWithinRadius
                                    ? t("map.kpi.in_radius", "In radius")
                                    : t("map.kpi.out_radius", "Out of radius")
                                }
                                color={row.isWithinRadius ? "success" : "warning"}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{row.imei ?? "—"}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SoftBox>
            </Card>
          </SoftBox>
        )}
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default TeacherDetail;
